/**
 * Placeholder data for SBI Learning + Program dashboard
 */

export const currentBootcamp = {
  id: 1,
  title: "EEG Bootcamp for Anaesthesia",
  purpose: "Master non-invasive EEG monitoring techniques for safer anaesthesia practice",
  coverImage: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800",
  progress: 45,
  nextLesson: "Module 3: EEG Signal Interpretation",
  lastAccessed: "2 hours ago",
  level: "ADVANCED",
  badges: ["monthly live sessions", "CME"],
};

export const brainSafeMomentum = {
  streak: 18,
  xp: 3420,
  nextMilestone: {
    name: "Expert Practitioner",
    progress: 68,
    target: 50,
    current: 34,
  },
  infoChip: "Non-invasive, patient-centred focus",
};

export const bootcampPathway = [
  {
    id: 1,
    step: 1,
    level: "BASIC",
    title: "EEG Fundamentals",
    description: "Introduction to EEG basics and signal acquisition",
    status: "completed",
    completedDate: "2026-01-15",
    objectives: [
      "Understand EEG signal characteristics",
      "Learn proper electrode placement",
      "Recognize common artifacts",
    ],
    quizzes: ["EEG Basics Quiz", "Signal Quality Assessment"],
    caseDiscussions: ["Case 1: Normal EEG Patterns", "Case 2: Artifact Recognition"],
    liveSessions: [],
  },
  {
    id: 2,
    step: 2,
    level: "ADVANCED",
    title: "Clinical Applications",
    description: "Applying EEG monitoring in clinical scenarios",
    status: "in-progress",
    progress: 45,
    objectives: [
      "Interpret EEG changes during anaesthesia",
      "Identify depth of anaesthesia indicators",
      "Apply monitoring protocols",
    ],
    quizzes: ["Clinical Interpretation Quiz"],
    caseDiscussions: ["Case 3: Depth Monitoring", "Case 4: Emergence Patterns"],
    liveSessions: [
      { date: "2026-02-05", time: "18:00", title: "Live Q&A Session" },
    ],
  },
  {
    id: 3,
    step: 3,
    level: "ULTIMATE",
    title: "Advanced Techniques",
    description: "Master-level EEG monitoring and optimization",
    status: "locked",
    objectives: [
      "Advanced signal processing",
      "Custom protocol development",
      "Research applications",
    ],
    quizzes: [],
    caseDiscussions: [],
    liveSessions: [
      { date: "2026-02-15", time: "18:00", title: "Masterclass Session" },
    ],
  },
];

export const upcomingEvents = [
  {
    id: 1,
    type: "Masterclass",
    title: "EEG Monitoring in High-Risk Patients",
    date: "2026-01-28",
    time: "18:00",
    duration: "90 min",
    instructor: "Dr. Sarah Chen",
  },
  {
    id: 2,
    type: "Podcast",
    title: "SBI Conversations: Patient Safety Stories",
    date: "2026-01-30",
    time: "14:00",
    duration: "45 min",
    instructor: "Dr. Michael Thompson",
  },
  {
    id: 3,
    type: "Bootcamp",
    title: "Advanced EEG Bootcamp Session 3",
    date: "2026-02-05",
    time: "18:00",
    duration: "120 min",
    instructor: "Dr. Sarah Chen",
  },
  {
    id: 4,
    type: "Masterclass",
    title: "Non-Invasive Monitoring Best Practices",
    date: "2026-02-10",
    time: "17:00",
    duration: "90 min",
    instructor: "Dr. Emily Rodriguez",
  },
  {
    id: 5,
    type: "Podcast",
    title: "Clinical Case Studies: EEG Interpretation",
    date: "2026-02-15",
    time: "16:00",
    duration: "60 min",
    instructor: "Dr. James Wilson",
  },
  {
    id: 6,
    type: "Bootcamp",
    title: "EEG Signal Processing Fundamentals",
    date: "2026-02-20",
    time: "19:00",
    duration: "105 min",
    instructor: "Dr. Sarah Chen",
  },
  {
    id: 7,
    type: "Masterclass",
    title: "Anesthesia Depth Monitoring Techniques",
    date: "2026-02-25",
    time: "15:30",
    duration: "75 min",
    instructor: "Dr. Michael Thompson",
  },
];

export const sbiCoreRecommendations = [
  {
    id: 1,
    title: "Pre-operative Assessment",
    phase: "Pre",
    type: "Guideline",
    summary: "Comprehensive pre-operative evaluation protocol",
    implementation: "Use standardized checklist before surgery",
  },
  {
    id: 2,
    title: "EEG Monitoring Setup",
    phase: "Pre",
    type: "Tool",
    summary: "Proper electrode placement and calibration",
    implementation: "Follow 10-20 system guidelines",
  },
  {
    id: 3,
    title: "Baseline Recording",
    phase: "Pre",
    type: "Checklist",
    summary: "Establish baseline EEG patterns",
    implementation: "Record 5-minute baseline before induction",
  },
  {
    id: 4,
    title: "Induction Protocol",
    phase: "Intra",
    type: "Guideline",
    summary: "EEG-guided induction procedures",
    implementation: "Monitor depth indicators during induction",
  },
  {
    id: 5,
    title: "Depth Monitoring",
    phase: "Intra",
    type: "Tool",
    summary: "Continuous depth of anaesthesia assessment",
    implementation: "Use real-time EEG analysis",
  },
  {
    id: 6,
    title: "Maintenance Checklist",
    phase: "Intra",
    type: "Checklist",
    summary: "Ongoing monitoring during maintenance",
    implementation: "Regular signal quality checks",
  },
  {
    id: 7,
    title: "Emergence Protocol",
    phase: "Post",
    type: "Guideline",
    summary: "EEG-guided emergence procedures",
    implementation: "Monitor emergence patterns",
  },
  {
    id: 8,
    title: "Post-operative Assessment",
    phase: "Post",
    type: "Tool",
    summary: "Post-operative EEG evaluation",
    implementation: "Document post-op findings",
  },
  {
    id: 9,
    title: "Recovery Checklist",
    phase: "Post",
    type: "Checklist",
    summary: "Post-operative recovery monitoring",
    implementation: "Follow recovery protocols",
  },
  // Add more to reach 18 total
  {
    id: 10,
    title: "Patient Positioning",
    phase: "Pre",
    type: "Guideline",
    summary: "Optimal positioning for EEG monitoring",
    implementation: "Ensure proper head positioning",
  },
  {
    id: 11,
    title: "Signal Quality Tool",
    phase: "Intra",
    type: "Tool",
    summary: "Real-time signal quality assessment",
    implementation: "Monitor impedance continuously",
  },
  {
    id: 12,
    title: "Artifact Recognition",
    phase: "Intra",
    type: "Guideline",
    summary: "Identify and manage artifacts",
    implementation: "Train on artifact patterns",
  },
  {
    id: 13,
    title: "Emergency Protocol",
    phase: "Intra",
    type: "Guideline",
    summary: "EEG changes during emergencies",
    implementation: "Rapid response procedures",
  },
  {
    id: 14,
    title: "Documentation Tool",
    phase: "Post",
    type: "Tool",
    summary: "EEG monitoring documentation",
    implementation: "Use standardized forms",
  },
  {
    id: 15,
    title: "Quality Assurance",
    phase: "Post",
    type: "Checklist",
    summary: "Post-procedure quality review",
    implementation: "Review monitoring data",
  },
  {
    id: 16,
    title: "Equipment Maintenance",
    phase: "Pre",
    type: "Checklist",
    summary: "Pre-use equipment checks",
    implementation: "Daily maintenance protocol",
  },
  {
    id: 17,
    title: "Team Communication",
    phase: "Intra",
    type: "Guideline",
    summary: "Effective communication during monitoring",
    implementation: "Use standardized communication",
  },
  {
    id: 18,
    title: "Patient Education",
    phase: "Pre",
    type: "Guideline",
    summary: "Educating patients about monitoring",
    implementation: "Provide patient information",
  },
];

export const programDashboards = [
  {
    id: 1,
    name: "SBI-Muda",
    title: "OR Efficiency & Process Metrics",
    description: "Monitor operating room efficiency and process optimization",
    metrics: {
      efficiency: 87,
      avgTime: "2.3h",
      utilization: 92,
    },
    sparklineData: [85, 88, 87, 89, 87, 90, 87],
  },
  {
    id: 2,
    name: "SBI-Us",
    title: "Staff & Patient Quality Snapshot",
    description: "Anonymous survey results and perceived quality metrics",
    metrics: {
      satisfaction: 4.6,
      responses: 124,
      quality: 91,
    },
    sparklineData: [4.4, 4.5, 4.6, 4.7, 4.6, 4.6, 4.6],
  },
];

export const sbiFacesVideos = [
  {
    id: 1,
    title: "Patient Story: Sarah's Experience",
    thumbnail: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400",
    duration: "5:32",
  },
  {
    id: 2,
    title: "Recovery Journey: John's Story",
    thumbnail: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400",
    duration: "4:18",
  },
  {
    id: 3,
    title: "Family Perspective: The Anderson Family",
    thumbnail: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400",
    duration: "6:45",
  },
  {
    id: 4,
    title: "Healthcare Provider Testimonial",
    thumbnail: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400",
    duration: "7:12",
  },
];

export const vciMediaCategories = [
  "All",
  "Tutorials",
  "Case Studies",
  "Research",
  "Webinars",
  "Interviews",
];

export const globalSociety = {
  countries: [
    "United States",
    "United Kingdom",
    "Canada",
    "Australia",
    "Germany",
    "France",
    "Netherlands",
    "Sweden",
    "Norway",
    "Japan",
  ],
  technologyPartners: [
    { name: "NeuroTech", logo: "https://via.placeholder.com/80x40?text=NT" },
    { name: "BrainWave", logo: "https://via.placeholder.com/80x40?text=BW" },
    { name: "SafeMonitor", logo: "https://via.placeholder.com/80x40?text=SM" },
  ],
};
